/**
 * Main Layout Component
 *
 * Uses Aaim institutional design system with TreeNav sidebar,
 * TopNavBar, Breadcrumbs, and GlobalSearch.
 */
import { useState, useMemo, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboardIcon,
  UsersIcon,
  CalculatorIcon,
  FileTextIcon,
  LandmarkIcon,
  BadgeDollarSignIcon,
  ShieldCheckIcon,
  TrendingUpIcon,
  MenuIcon,
  WorkflowIcon,
  FolderOpenIcon,
  ActivityIcon,
} from 'lucide-react';
import {
  CommandPalette,
  CommandPaletteTrigger,
  AppHeader,
  PortalFrame,
  ThemeToggle,
  UserProfileMenu,
  TreeNav,
  TreeNavSection,
  TreeNavItem,
  BuildVersion,
} from '@aaimplatform/ui';
import type {
  PaletteResult,
  PaletteCategory,
  UserProfile,
  UserMenuItem,
  BreadcrumbItem,
} from '@aaimplatform/ui';
import { useAuth } from '../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Sidebar width now lives with the frame that applies it: PORTAL_SIDEBAR_WIDTH
// in @aaimplatform/ui (packages/ui/src/shell/PortalFrame.tsx). Same 260px.

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

function buildBreadcrumbs(labels: string[], paths?: (string | undefined)[]): BreadcrumbItem[] {
  return labels.map((label, index) => {
    const isLast = index === labels.length - 1;
    return {
      label,
      href: isLast ? undefined : paths?.[index],
    };
  });
}

const PAGE_INFO: Record<string, { title: string; breadcrumbs: BreadcrumbItem[] }> = {
  '/dashboard': {
    title: 'Dashboard',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Dashboard'], ['/']),
  },
  '/clients': {
    title: 'Clients',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Clients'], ['/']),
  },
  '/calculator': {
    title: 'Borrowing Calculator',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Borrowing Calculator'], ['/']),
  },
  '/proposals': {
    title: 'Proposals',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Proposals'], ['/']),
  },
  '/proposals/new': {
    title: 'New Proposal',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Proposals', 'New Proposal'], ['/', '/proposals']),
  },
  '/lending/workflows': {
    title: 'Lending Workflows',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Lending', 'Workflows'], ['/']),
  },
  '/documents': {
    title: 'Documents',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Documents'], ['/']),
  },
  '/collateral': {
    title: 'Collateral Monitoring',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Collateral Monitoring'], ['/']),
  },
  '/pe/funds': {
    title: 'Fund Overview',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Private Equity', 'Fund Overview'], ['/', '/pe/funds']),
  },
  '/pe/lp-valuation': {
    title: 'LP Valuation',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Private Equity', 'LP Valuation'], ['/', '/pe/lp-valuation']),
  },
  '/pe/collateral': {
    title: 'Collateral Assessment',
    breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Private Equity', 'Collateral Assessment'], ['/', '/pe/collateral']),
  },
};

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

const SEARCH_CATEGORIES: PaletteCategory[] = [
  { id: 'pages', label: 'Pages', icon: FileTextIcon },
];

const SEARCHABLE_PAGES: PaletteResult[] = [
  { id: 'dashboard', title: 'Dashboard', category: 'pages', href: '/dashboard' },
  { id: 'clients', title: 'Clients', category: 'pages', href: '/clients' },
  { id: 'calculator', title: 'Borrowing Calculator', category: 'pages', href: '/calculator' },
  { id: 'proposals', title: 'Proposals', category: 'pages', href: '/proposals' },
  { id: 'lending-workflows', title: 'Lending Workflows', category: 'pages', href: '/lending/workflows' },
  { id: 'documents', title: 'Documents', category: 'pages', href: '/documents' },
  { id: 'collateral-monitoring', title: 'Collateral Monitoring', category: 'pages', href: '/collateral' },
  { id: 'connector', title: 'Mission Trust Connector', category: 'pages', href: '/connector' },
  { id: 'pe-funds', title: 'PE Fund Overview', category: 'pages', href: '/pe/funds' },
  { id: 'pe-lp-valuation', title: 'PE LP Valuation', category: 'pages', href: '/pe/lp-valuation' },
  { id: 'pe-collateral', title: 'PE Collateral Assessment', category: 'pages', href: '/pe/collateral' },
];

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Breadcrumbs
  const currentPageInfo = useMemo((): { title: string; breadcrumbs: BreadcrumbItem[] } => {
    // Exact match
    if (PAGE_INFO[location.pathname]) {
      return PAGE_INFO[location.pathname];
    }
    // Prefix match for dynamic routes (e.g. /clients/:clientId, /proposals/:id)
    if (location.pathname.startsWith('/clients/')) {
      return {
        title: 'Client Detail',
        breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Clients', 'Client Detail'], ['/', '/clients']),
      };
    }
    if (location.pathname.startsWith('/proposals/')) {
      return {
        title: 'Proposal',
        breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Proposals', 'Proposal'], ['/', '/proposals']),
      };
    }
    // Root ('/') redirects to /dashboard, so the fallback resolves to the
    // dashboard. Use the page's actual function ('Dashboard'), not the app name.
    return {
      title: 'Dashboard',
      breadcrumbs: buildBreadcrumbs(['Advisor Tools', 'Dashboard'], ['/']),
    };
  }, [location.pathname]);

  // User profile
  const userProfile: UserProfile = useMemo(() => ({
    name: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Advisor',
    email: user?.email || 'advisor@aaim.com',
    role: user?.role || 'Financial Advisor',
    initials: user?.firstName?.charAt(0) || user?.name?.charAt(0) || 'A',
  }), [user?.name, user?.firstName, user?.lastName, user?.email, user?.role]);

  const userMenuItems: UserMenuItem[] = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', onClick: () => navigate('/dashboard') },
  ], [navigate]);

  const handleSignOut = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Search
  const handleSearch = useCallback(async (query: string, category?: string): Promise<PaletteResult[]> => {
    const lowerQuery = query.toLowerCase();
    return SEARCHABLE_PAGES.filter(page => {
      const matchesQuery = page.title.toLowerCase().includes(lowerQuery);
      const matchesCategory = !category || page.category === category;
      return matchesQuery && matchesCategory;
    });
  }, []);

  const handleSearchSelect = useCallback((result: PaletteResult) => {
    if (result.href) {
      navigate(result.href);
    }
  }, [navigate]);

  // Nav active state
  const activeNavItemId = useMemo(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path.startsWith('/clients')) return 'clients';
    if (path.startsWith('/proposals')) return 'proposals';
    if (path.startsWith('/lending')) return 'lending-workflows';
    if (path === '/documents') return 'documents';
    if (path === '/collateral') return 'collateral-monitoring';
    if (path === '/connector') return 'connector';
    // Map pe/ paths to hyphenated nav IDs
    const peMatch = path.match(/^\/pe\/(.+)$/);
    if (peMatch) return `pe-${peMatch[1]}`;
    return path.replace(/^\//, '');
  }, [location.pathname]);

  const handleNavItemSelect = useCallback((id: string) => {
    const pathMap: Record<string, string> = {
      'dashboard': '/dashboard',
      'clients': '/clients',
      'calculator': '/calculator',
      'proposals': '/proposals',
      'lending-workflows': '/lending/workflows',
      'documents': '/documents',
      'collateral-monitoring': '/collateral',
      'connector': '/connector',
      'pe-funds': '/pe/funds',
      'pe-lp-valuation': '/pe/lp-valuation',
      'pe-collateral': '/pe/collateral',
    };
    const path = pathMap[id];
    if (path) {
      navigate(path);
      setMobileOpen(false);
    }
  }, [navigate]);

  // Build-time PE origin. The Private Equity group is hidden while
  // VITE_PE_VALUATION_URL is empty (operator ruling 2026-09-15: with no origin
  // every PE page rendered a visible error boundary). The prod Dockerfile bakes
  // the SPA's own host origin (https://advisor.aaim.com, GH #1671) so the
  // requests stay same-origin and reach pe-valuation through
  // the advisor.aaim.com/api/v1/pe/* load-balancer rule; the group opens on that
  // non-empty value. Truthiness mirrors PE_VALUATION_ORIGIN at
  // packages/types/src/api-clients.ts. Read at render rather than module scope
  // so the gate is observable to a test.
  const peValuationConfigured = Boolean(
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_PE_VALUATION_URL
  );

  // Sidebar nav content
  const treeNavContent = (
    <>
      {/* nav-id: advisoryNav */}
      <TreeNavSection id="advisory" label="Advisory">
        <TreeNavItem id="dashboard" label="Dashboard" icon={LayoutDashboardIcon} />
        <TreeNavItem id="clients" label="Clients" icon={UsersIcon} />
        <TreeNavItem id="calculator" label="Borrowing Calculator" icon={CalculatorIcon} />
        <TreeNavItem id="proposals" label="Proposals" icon={FileTextIcon} />
      </TreeNavSection>

      {/* nav-id: lendingNav */}
      <TreeNavSection id="lending" label="Lending">
        <TreeNavItem id="lending-workflows" label="Workflows" icon={WorkflowIcon} />
        <TreeNavItem id="documents" label="Documents" icon={FolderOpenIcon} />
        <TreeNavItem id="collateral-monitoring" label="Collateral Monitoring" icon={ActivityIcon} />
        <TreeNavItem id="connector" label="Mission Trust Connector" icon={ShieldCheckIcon} />
      </TreeNavSection>

      {/* nav-id: privateEquityAdvisorNav */}
      {/* flag-gate: peValuationConfigured */}
      {/* The three /pe/* routes stay registered and reachable by deep link; only
          the nav affordance is withheld while the origin is empty. */}
      {peValuationConfigured ? (
        <TreeNavSection id="private-equity" label="Private Equity">
          <TreeNavItem id="pe-funds" label="Fund Overview" icon={LandmarkIcon} />
          <TreeNavItem id="pe-lp-valuation" label="LP Valuation" icon={BadgeDollarSignIcon} />
          <TreeNavItem id="pe-collateral" label="Collateral Assessment" icon={ShieldCheckIcon} />
        </TreeNavSection>
      ) : null}
    </>
  );

  // Desktop sidebar contents. Passed into PortalFrame's `sidebar` slot; the
  // frame owns the <aside> chrome (fixed positioning, width, z-50, border).
  const desktopSidebar = (
    <>
      <div className="h-[65px] flex items-center px-4 border-b border-recessed">
        <img
          src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
          alt="Aaim"
          className="h-6"
        />
        <span className="ml-2 text-xs font-sans text-tertiary">
          Advisor Tools
        </span>
      </div>

      <TreeNav
        activeItemId={activeNavItemId}
        onItemSelect={handleNavItemSelect}
        density="default"
        searchable
        searchPlaceholder="Filter tools..."
        className="flex-1"
      >
        {treeNavContent}
      </TreeNav>

      {/* Bottom info */}
      <div className="p-4 border-t border-recessed">
        <div className="flex items-center gap-3 px-3 py-3 bg-elevated rounded-sm">
          <div className="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center flex-shrink-0">
            <TrendingUpIcon className="w-5 h-5 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-sans font-semibold text-primary truncate leading-normal">
              Advisor Tools
            </div>
            <div className="text-xs font-sans text-tertiary leading-normal">
              Wealth Advisory
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-recessed">
        <BuildVersion
          buildInfo={{ version: import.meta.env.VITE_BUILD_VERSION || '0.0.0-dev' }}
          format="short"
        />
      </div>
    </>
  );

  // Mobile drawer contents. Deliberately NOT the same node as `desktopSidebar`:
  // the shipping mobile tree omits `searchable`/`searchPlaceholder` and the
  // bottom info block. Reusing one node would change mobile navigation, which an
  // as-is extraction must not do.
  const mobileSidebar = (
    <>
      <div className="h-[65px] flex items-center px-4 border-b border-recessed">
        <img
          src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
          alt="Aaim"
          className="h-6"
        />
        <span className="ml-2 text-xs font-sans text-tertiary">
          Advisor Tools
        </span>
      </div>
      <TreeNav
        activeItemId={activeNavItemId}
        onItemSelect={handleNavItemSelect}
        density="default"
        className="flex-1"
      >
        {treeNavContent}
      </TreeNav>

      <div className="px-4 py-3 border-t border-recessed">
        <BuildVersion
          buildInfo={{ version: import.meta.env.VITE_BUILD_VERSION || '0.0.0-dev' }}
          format="short"
        />
      </div>
    </>
  );

  return (
    <PortalFrame
      overlays={
        /* Command Palette (global search) -- renders as a modal overlay */
        <CommandPalette
          categories={SEARCH_CATEGORIES}
          onSearch={handleSearch}
          onSelect={handleSearchSelect}
          placeholder="Search tools..."
        />
      }
      sidebar={desktopSidebar}
      mobileSidebar={mobileSidebar}
      mobileOpen={mobileOpen}
      onMobileClose={() => setMobileOpen(false)}
      header={
        <AppHeader
          title={currentPageInfo.title}
          leading={
            /* Sidebar toggle: top-left, BEFORE the title (it opens the sidebar
               drawer, so it sits where the sidebar is). Mobile only. */
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-sm hover:bg-elevated transition-colors"
              aria-label="Open navigation"
            >
              <MenuIcon className="w-5 h-5 text-secondary" strokeWidth={2} />
            </button>
          }
          actions={
            <>
              <CommandPaletteTrigger />
              {/* Placement convention (GH #2305): last icon affordance before
                  the user pill, on every institutional SPA. */}
              <ThemeToggle />
              <UserProfileMenu
                user={userProfile}
                menuItems={userMenuItems}
                onSignOut={handleSignOut}
                showEmail
                showRole
              />
            </>
          }
          sticky
          backdrop
        />
      }
      footer={
        <footer className="bg-surface border-t border-recessed px-4 md:px-6 py-4">
          <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row items-center justify-between gap-2 text-xs font-sans text-tertiary leading-normal">
            <span>2026 Aaim. All rights reserved.</span>
          </div>
        </footer>
      }
    >
      {/* The 1440px clamp stays with the portal: the four institutional layouts
          do not agree on it (the two admin portals have none). */}
      <div className="max-w-[1440px] min-w-0 w-full">
        <Outlet />
      </div>
    </PortalFrame>
  );
}
